import { useState, type ReactNode } from 'react';
import type {
  MetaGoalBucket,
  PortalChannelBlock,
  PortalSeriesPoint,
  PortalCompetitivePosition,
  PortalCreativeRow,
  PortalDemographics,
  PortalProvenanceLine,
  PortalReportLabels,
} from '../../../../../types/portalReport';
import type { ReportingDemographicsDimension } from '../../../../../types/reporting';
import {
  COMPETITIVE_COPY,
  audienceUnavailableForAccount,
  competitiveUnavailable,
  competitiveValueText,
  creativesUnavailable,
  deliveryDiagnosticsUnavailable,
  deliveryHistoryWithheld,
  hasCompetitivePosition,
  provenanceText,
} from '../../../../../lib/portalReport/reportCopy';
// ⚠️ PRESENTATION ONLY, and only here. The campaign table, the hero figures, the
// totals, the charts and the demographics all keep working on UNGROUPED ad rows
// — nothing that feeds a headline number goes through this. Read the module
// header before changing the rule.
import {
  creativeClubLabel,
  groupCreativeRows,
  mergedCreativeLabel,
} from '../../../../../lib/portalReport/creativeGrouping';
import {
  demographicsDenominatorNote,
  demographicsEmptyMessage,
  demographicsPeriodNote,
  sortRows,
} from '../../../../../lib/reporting/demographics';
// ⚠️ The STAFF panel's own vocabulary, imported rather than restated — the same
// device `portalReport/deltas.ts` uses for `computeDelta`. A client and their
// account manager must not be shown two different words for one breakdown
// value, and "Platform" meaning something else here would be exactly that.
import {
  demographicsAsOfNote,
  dimensionValueLabel,
  formatShare,
} from '../../../../reporting/demographicsView';
import {
  DASH,
  formatReportDate,
  formatReportInstant,
  metricFormat,
  moneyFull,
  moneyRound,
  numberFull,
} from './format';
import { SectionHeading, SwitchGroup } from './controls';
import { ReportChartCard } from './ReportChartCard';
import {
  type DeliveryCampaignLike,
  type DeliveryCurrencySlice,
  type DeliveryMetric,
  deliveryFigures,
  deliverySeriesValues,
  hasEnoughHistory,
  deliveryGroups,
} from '../../../../../lib/portalReport/delivery';
import { type ChartGrain } from '../../../../../lib/portalReport/chartPlan';
import {
  costPerOrNull,
  creativeGridIntro,
  creativeMetricsFor,
  filterableGoalBucket,
  filteredAudienceEmptyMessage,
  type CreativeMetric,
} from '../../../../../lib/portalReport/metaGoalReport';
import type { GroupedCreativeRow } from '../../../../../lib/portalReport/creativeGrouping';

// CLIENT PORTAL RESTYLE — the panels that are not charts or tables.
//
// ⚠️⚠️ OMIT A SECTION WITH NO DATA SOURCE; EXPLAIN A SECTION WITH A SOURCE AND A
// REASON — corrected 2026-08-31, after the earlier rule ("every one of these
// omits itself entirely when its data is genuinely unavailable") cost four
// rounds of investigation.
//
// What happened: a nightly demographics job had been failing silently, the read
// core discarded 29 days of good data over one missing night, and THIS FILE then
// deleted the audience section with no explanation. Three silent failures
// stacked, and the only signal anywhere in the system was a human noticing a
// panel was missing on one club.
//
// So: no placeholder, no em-dash grid, no "coming soon" — but a SENTENCE saying
// why, wherever the data source exists and produced nothing. The model is
// `yearOnYear.previousUnavailableReason`, which explains its own absence in full
// in the same payload. Every sentence lives in the pure `reportCopy` /
// `demographics` cores, so each is asserted without jsdom.
//
// ⚠️ The other half still holds: a section with NO SOURCE is omitted outright.
// Sales and cost per sale are absent until the CRM exists, and a placeholder
// there would promise a feature rather than explain an absence.

// ---------------------------------------------------------------------------
// Google — competitive position
// ---------------------------------------------------------------------------

/**
 * ⚠️ PROMINENT SPACE, where Meta's delivery metrics are collapsed behind a
 * disclosure. These are not the same kind of number: "you lost 15.7% of
 * impressions to budget" is a decision; a CPM is not.
 *
 * ⚠️ NO ARITHMETIC ON TOP. The "recovering that share is worth roughly N more
 * conversions" line is the most commercially valuable sentence available here
 * and the most likely to be read as a promise. It awaits an explicit yes from
 * Scott and is deliberately absent.
 */
export function CompetitivePanel({ competitive }: { competitive: PortalCompetitivePosition }) {
  // ⚠️ ALL THREE NULL and the CARDS are not drawn — three dashes tell a client
  // nothing and read as a fault. ⚠️ But the section SAYS WHY rather than
  // vanishing: impression share has a real source here, and "not measurable for
  // Performance Max" is a fact about their campaign mix that a client can act
  // on, where a missing heading is just a hole.
  if (!hasCompetitivePosition(competitive)) {
    return (
      <>
        <SectionHeading title="Competitive position" />
        <p className="rpt-note rpt-muted">{competitiveUnavailable()}</p>
      </>
    );
  }
  const cards = [
    { key: 'impressionShare' as const, value: competitive.impressionShare },
    { key: 'budgetLostShare' as const, value: competitive.budgetLostShare },
    { key: 'rankLostShare' as const, value: competitive.rankLostShare },
  ];
  return (
    <>
      <SectionHeading
        title="Competitive position"
        intro="How often the ads could have shown and did not. This is the one place a number here translates straight into a decision."
      />
      <div className="rpt-card-grid rpt-card-grid--3">
        {cards.map((c) => (
          <div key={c.key} className="rpt-card">
            <span className="rpt-mono">{COMPETITIVE_COPY[c.key].label}</span>
            {/* ⚠️ A share metric's ZERO means "not applicable", not 0%. Google
                writes 0.0 where a share cannot apply, so the query layer nulls
                the impossible ones and this must never dress a null as a
                measured zero. */}
            <p className="rpt-card-figure">{competitiveValueText(c.value)}</p>
            <p className="rpt-sub rpt-muted">{COMPETITIVE_COPY[c.key].explain}</p>
          </div>
        ))}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Meta — creatives
// ---------------------------------------------------------------------------

export function CreativeGrid({
  rows,
  accounts,
  labels,
  hadSpend,
  goal = null,
  filter = null,
}: {
  rows: PortalCreativeRow[];
  /**
   * The report's own accounts, for the per-card club label.
   *
   * ⚠️ THE LIST rather than a count — the label needs the NAME, and rule 12
   * forbids inferring one from an id. Its length is also what gates the label
   * off on a single-club report. See `creativeClubLabel`.
   */
  accounts: ReadonlyArray<{ id: string; name: string }>;
  labels: PortalReportLabels;
  /**
   * Whether the period had campaign spend at all.
   *
   * ⚠️ It decides WHICH absence sentence is true, and the two are different
   * facts about the client's month: "nothing ran" is not a fault, "ads ran but
   * Meta returned no creative detail" is a gap at our end. Collapsing them into
   * one vague sentence would misdescribe one of them every time.
   */
  hadSpend: boolean;
  /**
   * The goal-split page's filter state, or null everywhere else.
   *
   * ⚠️ NON-NULL SWITCHES THE MEASURES to the filtered goal's own (the panels'
   * cost-per rule — see `creativeMetricsFor`). Null leaves the grid exactly as
   * it has always been.
   */
  goal?: { bucket: MetaGoalBucket | null } | null;
  /** The shared goal-filter control, rendered in this section's heading. */
  filter?: ReactNode;
}) {
  const metrics = goal ? creativeMetricsFor(filterableGoalBucket(goal.bucket)) : null;
  if (rows.length === 0) {
    // ⚠️ SAYS SO rather than vanishing. An empty creative grid on a month that
    // genuinely had no ads is a perfectly good answer — but only if it is given.
    return (
      <>
        <SectionHeading title="The ads that ran" aside={filter ?? undefined} />
        <p className="rpt-note rpt-muted">{creativesUnavailable(hadSpend)}</p>
      </>
    );
  }
  return (
    <>
      <SectionHeading
        title="The ads that ran"
        // ⚠️ The merged-card clause is CUT because the card already carries its
        // AD COUNT ("3 ads combined") — a number is shorter than an explanation
        // and it is what the reader actually reconciles against. Verified before
        // cutting; see `mergedCreativeLabel`.
        intro={
          metrics
            ? creativeGridIntro(metrics)
            : 'Spend, leads and cost per lead for each creative, with its current delivery status.'
        }
        aside={filter ?? undefined}
      />
      <div className="rpt-creatives">
        {groupCreativeRows(rows).map((c) => {
          // ⚠️⚠️ SAFE TO LABEL ONLY BECAUSE THE CLUB IS A MERGE-KEY SEGMENT (§7).
          // Before that, the same templated ad name at six clubs merged onto one
          // card, so a single club name here would have been a figure labelled
          // with the wrong source.
          const club = creativeClubLabel(accounts, c.accountId);
          return (
            <div key={c.key}>
            <div className="rpt-thumb">
              {c.thumbnailUrl ? (
                // ⚠️ Proxied through our own origin server-side — the stored
                // facebook.com/ads/image URL matches standard ad-blocker filter
                // lists, and a client running uBlock would see a wall of "No
                // preview" and conclude the tool is broken.
                <img src={c.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                // ⚠️ A placeholder, never a broken <img>. An aged-out CDN URL
                // is a NORMAL outcome on a report that shows history.
                <span className="rpt-mono">No preview</span>
              )}
            </div>
            {/* ⚠️ ABSENT when the pull recorded no status — never defaulted to
                "Paused", which would tell a client an ad is off when it may be
                running. And there is no FORMAT pill: format was cut, not
                deferred, and must not be inferred from a thumbnail. */}
            {c.status && <span className="rpt-pill">{c.status}</span>}
            {/* ⚠️ ABOVE the ad name, because the ad name is TEMPLATED and repeats
                across clubs — the club is what tells two otherwise identical cards
                apart, so it has to be read first. Absent entirely on a
                single-club scope, and absent (never guessed) when the id does
                not resolve. Truncated with a `title` rather than wrapped: a long
                name must not push the card out of the grid. */}
            {club && (
              <p className="rpt-creative-club rpt-muted" title={club}>
                {club}
              </p>
            )}
            <p className="rpt-creative-name">{c.adName ?? c.campaignName ?? c.key}</p>
            {/* ⚠️⚠️ THE CARD STATES WHAT IT MERGES. Without it a client cannot
                reconcile this grid against the campaign table — three ads' spend
                under one heading — and will assume one of the two is wrong. */}
            {mergedCreativeLabel(c) && (
              // ⚠️ `rpt-muted` carries the COLOUR (and the dark-band override
              // that goes with it); `rpt-creative-merge` carries only the size
              // and spacing. See the CSS — reading the token directly here put
              // this at 2.26:1 on a contrast band.
              <p className="rpt-creative-merge rpt-muted">{mergedCreativeLabel(c)}</p>
            )}
            <div className="rpt-crow">
              <span>Spend</span>
              <b>{moneyRound(c.spend, c.currency)}</b>
            </div>
            {metrics ? (
              metrics.map((m) => (
                <div key={m.key} className="rpt-crow">
                  <span>{m.label}</span>
                  <b>{creativeMetricText(c, m)}</b>
                </div>
              ))
            ) : (
              <>
                <div className="rpt-crow">
                  <span>{labels.leadsShort}</span>
                  <b>{numberFull(c.leads)}</b>
                </div>
                <div className="rpt-crow">
                  <span>{labels.costPerLead}</span>
                  <b>{c.costPerLead === null ? DASH : moneyFull(c.costPerLead, c.currency)}</b>
                </div>
              </>
            )}
            </div>
          );
        })}
      </div>
    </>
  );
}

/**
 * One goal-split measure on a creative card. ⚠️ An ABSENT count is a dash, never
 * a 0, and a cost-per of nothing is a dash (never ∞, never 0).
 */
function creativeMetricText(c: GroupedCreativeRow, m: CreativeMetric): string {
  const countOf = (k: 'leads' | 'purchases' | 'landingPageViews'): number | null =>
    k === 'leads' ? c.leads : k === 'purchases' ? c.purchases : c.landingPageViews;
  if (m.kind === 'count') {
    const v = countOf(m.key);
    return v === null ? DASH : numberFull(v);
  }
  const denominator =
    m.key === 'costPerLead' ? c.leads : m.key === 'costPerPurchase' ? c.purchases : c.landingPageViews;
  if (denominator === null) return DASH;
  const v = costPerOrNull(c.spend, denominator);
  return v === null ? DASH : moneyFull(v, c.currency);
}

/**
 * Delivery diagnostics — its own band of five CHART CARDS, on BOTH the Meta and
 * Google pages.
 *
 * ⚠️⚠️ THE FRAMING SENTENCE DOES MORE WORK HERE THAN ANYWHERE ELSE ON THE PAGE,
 * and it does more of it at this size than it did as five sparklines. Five full
 * chart cards is the SAME visual treatment "Meta advertising results" gets, so
 * the risk shifts from the WORDS to the LAYOUT: a section that looks as
 * important as the results section reads as results, whatever its intro says.
 * Brand rule 7 — vanity metrics never as an OUTCOME — now rests on this
 * sentence and on the hierarchy around it.
 *
 * ⚠️⚠️ IF IT READS AS A SECOND RESULTS SECTION, THE FIX IS HIERARCHY — a
 * lighter surface, a shorter chart, or the section moved further down — NOT a
 * longer caveat. Two rounds ago exactly that kind of sentence was removed. No
 * new explanatory copy belongs in this band: if it needs explaining at this
 * size, the size is wrong.
 *
 * ⚠️ PLACEMENT DIFFERS BY PAGE, DELIBERATELY. On Meta it follows the creative
 * grid; on Google it is LAST, after "What people searched for" — deliberately
 * far from the competitive panel, because impression SHARE and impressionS are
 * different measures and a reader who conflates them will read a percentage as
 * a count.
 */
export function DeliveryDiagnostics({
  campaigns,
  block,
  labels,
  periodLabel,
}: {
  campaigns: readonly DeliveryCampaignLike[];
  /** The channel's own block — the five charts read its two series. */
  block: PortalChannelBlock;
  labels: PortalReportLabels;
  periodLabel: string;
}) {
  // ⚠️⚠️ THE GRAIN IS BAND-LEVEL AND THE FORM IS PER CARD — a deliberate SPLIT
  // rather than an inconsistency, and the two halves have different reasons.
  //
  // GRAIN is one question asked of the whole band: a reader wants "the last
  // twelve months of delivery", not twelve months of one metric against the
  // reporting period for another, and cards on different windows cannot be
  // compared down a column.
  //
  // FORM is a per-chart judgement. `DEFAULT_FORM` opens counts as bars and
  // ratios as lines, so a band-level switch had to carry a third "Auto" state to
  // say "each chart in the shape that suits it" — which is not a state anybody
  // wants to press, and made the control read as a mode selector for something
  // it was not. With the form back on the card the default IS the behaviour,
  // and each card's switch shows what that card is actually doing.
  const [grain, setGrain] = useState<ChartGrain>('months');

  // ⚠️ WITH NO CAMPAIGNS THERE IS NO SOURCE for a delivery breakdown — nothing
  // to explain — so the band is omitted outright. With campaigns but no recorded
  // delivery there IS a source and it produced nothing, which is a different
  // statement and gets one.
  if (campaigns.length === 0) return null;
  const figures = deliveryFigures(campaigns);

  const series = grain === 'period' ? block.periodSeries : block.yearOnYear.current;

  return (
    <>
      <SectionHeading
        title="Delivery"
        intro="These describe how the ads were delivered, not what they returned. Useful for a conversation about reach and fatigue; not a measure of performance."
        aside={
          figures.hasDelivery ? (
            <div className="rpt-control-group">
              <SwitchGroup
                ariaLabel="Chart period"
                value={grain}
                onChange={setGrain}
                options={[
                  { value: 'months' as const, label: '12 months' },
                  { value: 'period' as const, label: 'Reporting period' },
                ]}
              />
            </div>
          ) : undefined
        }
      />
      {!figures.hasDelivery ? (
        <p className="rpt-note rpt-muted">{deliveryDiagnosticsUnavailable()}</p>
      ) : (
        /* ⚠️ TWO PER ROW, and five does not divide by two. Cost per click takes
           the full width because it is the COMPOSITE of the two above it (cost
           per 1,000 and click-through rate) — a reason a reader can see in the
           layout, which is what stops the odd one out looking like a mistake.
           ⚠️ Independent scales throughout: nothing is drawn across cards. */
        <div className="rpt-diag-cards">
          {/* ⚠️ THE ORDER COMES FROM `deliveryGroups()` — counts, then ratios —
              rather than from the flat catalogue. In a two-column grid that puts
              the two counts on the first row and the three ratios below, so the
              grouping is a property of the data rather than an accident of the
              layout. */}
          {deliveryGroups()
            .flatMap((group) => group.metrics)
            .map((metric) => (
              <DeliveryCard
                key={metric.key}
                metric={metric}
                figures={figures}
                block={block}
                series={series}
                grain={grain}
                labels={labels}
                periodLabel={periodLabel}
              />
            ))}
        </div>
      )}
    </>
  );
}

function DeliveryCard({
  metric,
  figures,
  block,
  series,
  grain,
  labels,
  periodLabel,
}: {
  metric: DeliveryMetric;
  figures: ReturnType<typeof deliveryFigures>;
  block: PortalChannelBlock;
  series: readonly PortalSeriesPoint[];
  grain: ChartGrain;
  labels: PortalReportLabels;
  periodLabel: string;
}) {
  const values = deliverySeriesValues(series, metric.key);
  const measured = values.filter((v) => v !== null).length;
  return (
    <ReportChartCard
      className={metric.key === 'costPerClick' ? 'rpt-diag-wide' : undefined}
      title={metric.label}
      source={{ kind: 'delivery', block }}
      metrics={[metric.key]}
      labels={labels}
      currency={metric.kind === 'money' ? (figures.byCurrency[0]?.currency ?? 'AUD') : 'AUD'}
      periodLabel={periodLabel}
      grain={grain}
      /* ⚠️ NO `form` — the card owns it, so its default is `DEFAULT_FORM`'s
         per-metric rule and its switch shows what this chart is doing. */
      headline={
        <div className="rpt-diag-headline">
          <span className="rpt-diag-value">{deliveryFigureText(metric, figures)}</span>
          <span className="rpt-mono">{metric.label}</span>
        </div>
      }
      chartWithheld={
        hasEnoughHistory(values) ? null : deliveryHistoryWithheld(measured, grain)
      }
    />
  );
}

/**
 * ⚠️ MONEY IS WRITTEN PER CURRENCY, never blended — the report's rule
 * everywhere else, and the fix for a latent misstatement in the disclosure this
 * band replaces. A currency whose ratio is undefined is dropped rather than
 * printed as "A$—", and if every one is, the whole figure is a dash.
 */
function deliveryFigureText(
  metric: DeliveryMetric,
  figures: ReturnType<typeof deliveryFigures>,
): string {
  // ⚠️⚠️ THE SAME FORMATTER THE AXIS AND THE TOOLTIP USE. The figure was already
  // right when the axis was rounding to integers; keeping them one function is
  // what stops them drifting apart again, in either direction.
  const fmt = (currency: string) => metricFormat(metric.key, currency).value;
  switch (metric.key) {
    case 'impressions':
      return fmt('AUD')(figures.impressions);
    case 'clicks':
      return fmt('AUD')(figures.clicks);
    case 'ctr':
      return figures.ctr === null ? DASH : fmt('AUD')(figures.ctr);
    case 'costPerThousand':
      return moneyPerCurrency(figures.byCurrency, (s) => s.costPerThousand, fmt);
    case 'costPerClick':
      return moneyPerCurrency(figures.byCurrency, (s) => s.costPerClick, fmt);
  }
}

function moneyPerCurrency(
  slices: readonly DeliveryCurrencySlice[],
  pick: (slice: DeliveryCurrencySlice) => number | null,
  fmt: (currency: string) => (v: number) => string,
): string {
  const parts = slices
    .map((s) => {
      const value = pick(s);
      return value === null ? null : fmt(s.currency)(value);
    })
    .filter((p): p is string => p !== null);
  return parts.length ? parts.join(' · ') : DASH;
}

// ---------------------------------------------------------------------------
// Meta — audience
// ---------------------------------------------------------------------------

const DIMENSION_TITLE: Record<string, string> = {
  age: 'By age',
  gender: 'By gender',
  publisher_platform: 'Instagram against Facebook',
};

/** The three we show a client. The pull stores five; device and region are not asked for here. */
const SHOWN_DIMENSIONS = ['publisher_platform', 'age', 'gender'] as const;

/**
 * ⚠️⚠️ THE STATED PERIOD IS THE STORED WINDOW, NEVER THE SELECTED RANGE. Meta
 * reports breakdowns per PULL WINDOW rather than per day, so every other panel
 * on the page may say "1–15 Aug" while this one is describing 1 Jul – 27 Aug.
 * Widening the label to match the report is the ONE change that turns a correct
 * panel into a misleading one, so the note names BOTH periods and LEADS the
 * panel — a caveat read after a number has been believed is not a caveat.
 *
 * ⚠️ INSTAGRAM VS FACEBOOK CARRIES EXACTLY THE SAME CAVEAT as age and gender.
 * It is a demographics breakdown, not a daily fact, and presenting it beside the
 * daily charts without that note would invite the reader to reconcile it with
 * the headline.
 *
 * ⚠️ IT DOES NOT RECONCILE WITH THE HEADLINE, and says so: Meta withholds
 * unknown buckets and dedups across breakdowns, so every share is of THE
 * BREAKDOWN'S OWN TOTAL, and that denominator is printed above the figures.
 */
/**
 * ⚠️⚠️ THIS PANEL IS SINGLE-LOCATION ONLY, AND IT NO LONGER KNOWS THE ACCOUNT
 * COUNT (round 8 §3, 2026-09-04). A group report OMITS the section entirely — see
 * `RestyledReport`, where the scope is already known.
 *
 * ⚠️⚠️ THE PROP WAS REMOVED RATHER THAN LEFT AT 1, and that is the point of the
 * change rather than tidying. With no `locationCount` this component CANNOT
 * branch on group-ness, so the two absences are structurally unable to share a
 * gate:
 *
 *   — a GROUP's absence is STRUCTURAL AND PERMANENT (Meta reports per pull
 *     window, so two locations can be answered by two different periods and
 *     summing them mixes grains inside one percentage). Nobody can chase it.
 *   — a SINGLE LOCATION's is A FAULT SOMEONE SHOULD CHASE — a failed nightly
 *     demographics job, or a read that returned `grain: 'none'` with a reason.
 *
 * ⚠️⚠️ EVERY BRANCH BELOW IS UNTOUCHED, and a `return null` must never be added
 * to any of them. Both of them explain themselves. That property was bought at
 * a price: a GROUP report used to say why its audience section was absent while
 * a SINGLE-LOCATION one said nothing at all — backwards — and that silence is
 * what turned a failed nightly job into FOUR ROUNDS OF INVESTIGATION on
 * 2026-08-31.
 */
export function AudiencePanel({
  demographics,
  today,
  goalBucket = null,
  filter = null,
}: {
  demographics: PortalDemographics | null;
  today: string;
  /**
   * The goal filter applied to this read, or null. ⚠️ Only the goal-split page
   * passes one; it decides the filtered-empty sentence and the intro.
   */
  goalBucket?: MetaGoalBucket | null;
  /** The shared goal-filter control, rendered in every heading this panel draws. */
  filter?: ReactNode;
}) {
  const aside = filter ?? undefined;
  const bucket = filterableGoalBucket(goalBucket);
  // ⚠️⚠️ A NULL READ IS A FAULT TO CHASE, and it says so. This is the branch the
  // group case used to share; it is now the only one.
  if (!demographics) {
    return (
      <>
        <SectionHeading title="Who is answering" aside={aside} />
        <p className="rpt-note rpt-muted">{audienceUnavailableForAccount()}</p>
      </>
    );
  }
  const shown = SHOWN_DIMENSIONS.map((d) =>
    demographics.dimensions.find((dim) => dim.dimension === d),
  ).filter((d): d is ReportingDemographicsDimension => !!d && d.rows.length > 0);

  // ⚠️⚠️ THE FILTERED-EMPTY STATE SAYS WHY. Filtered to Purchase or Other there
  // are often no leads at all, and every share is then a dash — which, unexplained,
  // is indistinguishable from the panel breaking. (There is no reliability floor
  // to cite: the demographics floors were removed 2026-08-31 and must not return.)
  // ⚠️ Not when no window answers the range at all — that reason is the
  // unfiltered one and is stated below, not blamed on the filter.
  const filteredEmpty =
    demographics.period.grain === 'none'
      ? null
      : filteredAudienceEmptyMessage(
          bucket,
          shown.reduce((max, d) => Math.max(max, d.totals.leads), 0),
        );
  if (filteredEmpty !== null) {
    return (
      <>
        <SectionHeading title="Who is answering" aside={aside} />
        <p className="rpt-note rpt-muted">{filteredEmpty}</p>
      </>
    );
  }

  // ⚠️⚠️ THE GUARD THAT ACTUALLY FIRED IN THE INCIDENT. On the Pyrmont report
  // `demographics` was NOT null — the read succeeded and returned
  // `grain: 'none'` with a reason — and this `return null` threw the reason away
  // along with the heading. `demographicsEmptyMessage` had produced the sentence
  // all along; the staff Audience page has shown it since the day it shipped.
  if (shown.length === 0) {
    return (
      <>
        <SectionHeading title="Who is answering" aside={aside} />
        <p className="rpt-note rpt-muted">
          {demographics.period.grain === 'none'
            ? demographicsEmptyMessage(
                demographics.period.reason,
                demographics.range,
                formatReportDate,
              )
            : // A window WAS chosen but carried no rows in the three cuts we show
              // a client — rare, and a different statement from "no window covers
              // this range".
              'The audience breakdown for this period has no age, gender or network rows to show.'}
        </p>
      </>
    );
  }

  const note = demographicsPeriodNote(
    demographics.period,
    demographics.range,
    formatReportDate,
    today,
  );

  return (
    <>
      <SectionHeading
        title="Who is answering"
        intro={
          bucket
            ? 'Age, gender and the network the ads ran on, for the people who enquired through these campaigns. This breakdown counts leads, not purchases or landing page views.'
            : 'Age, gender and the network the ads ran on, for the people who enquired.'
        }
        aside={aside}
      />
      {/* ⚠️ THE TWO NOTES SHARE ONE ROW, and both still LEAD the panel — a
          caveat read after a number has been believed is not a caveat.
          ⚠️ They WRAP AND STACK on a narrow viewport rather than truncating: a
          pulled-at timestamp that ellipsises is worse than a second line, since
          the whole point of the second note is the time it names. */}
      <div className="rpt-note-row">
        {note && (
          <p className="rpt-note rpt-muted">
            <strong>Audience data</strong> {note}
          </p>
        )}
        {/* ⚠️⚠️ THE SNAPSHOT CAVEAT, RENDERED UNCONDITIONALLY — including when we
            cannot read the pull time. Demographics only advance when the
            demographics pass runs, and an absent caveat reads as live. This
            matters more here than on the staff panel: a stale audience section
            on a CLIENT report is worse than no audience section, and it goes
            stale silently. */}
        <p className="rpt-note rpt-muted">
          {demographicsAsOfNote(demographics.asOf, formatReportInstant)}
        </p>
      </div>
      <div className="rpt-card-grid rpt-card-grid--3">
        {shown.map((dim) => (
          <DimensionCard key={dim.dimension} dimension={dim} />
        ))}
      </div>
    </>
  );
}

function DimensionCard({ dimension }: { dimension: ReportingDemographicsDimension }) {
  const rows = sortRows(dimension.dimension, [...dimension.rows]);
  const max = Math.max(...rows.map((r) => r.leads), 1);
  return (
    <div className="rpt-card">
      <span className="rpt-mono">{DIMENSION_TITLE[dimension.dimension] ?? dimension.dimension}</span>
      {/* ⚠️ THE DENOMINATOR IS PRINTED ABOVE THE FIGURES. Every share below is
          a share of THIS breakdown's own total, which will not equal the
          headline — and a reader who spots that gap unaided stops trusting the
          page. */}
      <p className="rpt-sub rpt-muted">{demographicsDenominatorNote(dimension)}</p>
      <div className="rpt-bars">
        {rows.map((r) => (
          <div key={r.value} className="rpt-bar-row">
            <span className="rpt-muted">{dimensionValueLabel(dimension.dimension, r.value)}</span>
            <span className="rpt-bar-track">
              <span className="rpt-bar-fill" style={{ width: `${(r.leads / max) * 100}%` }} />
            </span>
            <span className="rpt-num">
              {numberFull(r.leads)}
              {' · '}
              {/* ⚠️ A share below the reliability floor is a DASH; the COUNT
                  still shows, so the reader can judge for themselves. Formatted
                  by the staff panel's own `formatShare`, so the two cannot
                  disagree about what a suppressed share looks like. */}
              {formatShare(r.leadShare)}
            </span>
          </div>
        ))}
      </div>
      {/* ⚠️ NO FLOOR NOTE. Every share is now shown at every size, so there is
          nothing to explain — and the denominator note ABOVE the figures is what
          makes that honest. See rule 4 in the read core. */}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * ⚠️ SHOWN TO THE CLIENT. A brand rule for monthly reports, and stronger on a
 * live page than in a PDF: silence about freshness implies live, and the two
 * channels are not equally fresh.
 */
export function ProvenanceBlock({ lines }: { lines: PortalProvenanceLine[] }) {
  if (lines.length === 0) return null;
  return (
    <>
      <span className="rpt-mono">Where these figures came from</span>
      <ul className="rpt-provenance">
        {lines.map((l) => (
          <li key={l.what}>{provenanceText(l, formatReportDate)}</li>
        ))}
      </ul>
    </>
  );
}

// ---------------------------------------------------------------------------
// Channel summary cards (Overview)
// ---------------------------------------------------------------------------

/**
 * ⚠️ THE CHANNEL MARK IS A SWATCH, NOT A LOGO — a decision, not a placeholder.
 * Meta's partner guidance requires prior Brand Review for any use of its assets;
 * Google requires approved artwork and forbids implying affiliation. Naming the
 * platform in words is referential use and clears both, and the swatch teaches
 * the chart colour at the same time.
 */
export function ChannelCard({
  title,
  channelRole,
  block,
  labels,
  otherBlock,
}: {
  title: string;
  channelRole: 'a' | 'b';
  block: PortalChannelBlock;
  otherBlock: PortalChannelBlock;
  labels: PortalReportLabels;
}) {
  const totals = block.totals[0] ?? null;
  const other = otherBlock.totals[0] ?? null;
  const shareOf = (mine: number | undefined, theirs: number | undefined): string => {
    if (mine === undefined || theirs === undefined) return DASH;
    const total = mine + theirs;
    return total === 0 ? DASH : `${Math.round((mine / total) * 100)}%`;
  };
  return (
    <div className="rpt-card">
      <div className="rpt-channel-head">
        <span
          className="rpt-channel-dot"
          style={{ background: channelRole === 'a' ? 'var(--rpt-chan-a)' : 'var(--rpt-chan-b)' }}
        />
        <h3 className="rpt-h3 rpt-display">{title}</h3>
      </div>
      <p className="rpt-card-figure">
        {totals ? moneyRound(totals.spend, totals.currency) : DASH}
      </p>
      <div className="rpt-crow">
        <span>{labels.leadsShort}</span>
        <b>{totals ? numberFull(totals.leads) : DASH}</b>
      </div>
      <div className="rpt-crow">
        <span>{labels.costPerLead}</span>
        <b>
          {totals?.costPerLead == null ? DASH : moneyFull(totals.costPerLead, totals.currency)}
        </b>
      </div>
      <div className="rpt-crow">
        <span>Share of spend</span>
        <b>{shareOf(totals?.spend, other?.spend)}</b>
      </div>
      <div className="rpt-crow">
        <span>Share of {labels.leadsShort.toLowerCase()}</span>
        <b>{shareOf(totals?.leads, other?.leads)}</b>
      </div>
    </div>
  );
}
